import { gridNumberComparator, gridStringOrNumberComparator } from '@mui/x-data-grid-pro';
import { NumberFormatPreset } from '@procureai/ui-mui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@procureai/uix';
import { CheckCircle2, CircleX, InfoIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractDemandCasesQuery, TsrctIssueStatus, TsrctSourcingCaseStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  CurrencyCellValue,
  DataGridConfig,
  DataGridLinkVariant,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  GridSortDirectionType,
  LinkCell,
  LinkCellValue,
  NumberCell,
  TooltipCell,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { IconCell } from '~/components/DataGridCustom/cells/IconCell/IconCell';
import { SourcingCaseStatusChip } from '~/components/SourcingCaseStatusChip/SourcingCaseStatusChip.tsx';
import { RouteId } from '~/routes';
import { eventStopPropagation } from '~/util/function/eventStopPropagation';

import { DemandCasesDataGridField } from './ContractDemandCaseListWidget.types';

type RowType = ContractDemandCasesQuery['tesseract']['contractDemand']['sourcingCases'][number];

export const useContractDemandCasesDataGridConfig: UseDataGridConfig<RowType> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandCasesDataGrid.column' });
  /*   const { t: tCell } = useTranslation(undefined, { keyPrefix: 'ContractDemandCasesDataGrid.cell' }); */
  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });

    return {
      sortModel: [{ field: DemandCasesDataGridField.SEQUENCE_NUMBER, sort: GridSortDirectionType.DESC }],
      columnVisibilityModel: {
        [DemandCasesDataGridField.ISSUES_OPEN_COUNT]: false,
        [DemandCasesDataGridField.ISSUES_COMPLETED_COUNT]: false,
        [DemandCasesDataGridField.LATEST_COMMENT]: false,
      },
      columns: [
        createColumn({
          field: DemandCasesDataGridField.SEQUENCE_NUMBER,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.sourcingCaseDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 200,
          hideable: false,
          sortComparator: (v1, v2, cellParams1, cellParams2) => {
            return gridStringOrNumberComparator(v1?.label, v2?.label, cellParams1, cellParams2);
          },
        }),
        createColumn({
          field: DemandCasesDataGridField.SUPPLIER,
          minWidth: 350,
          valueGetter: (_value, row) => row.supplier?.name,
          sortable: false,
        }),
        createColumn({
          field: DemandCasesDataGridField.STATUS,
          minWidth: 150,
          renderCell: cellFactory(SourcingCaseStatusChip, ({ row }) => ({
            status: row.status,
            cancellationReason: row.cancellationReason,
            cancellationReasonText: row.cancellationReasonText,
          })),
          valueGetter: (_value, row) => {
            if (!row.status) return null;
            const label = t(`SourcingCaseStatus.${row.status}`);
            if (row.cancellationReason) {
              const reason = t(`CaseCancellationReason.${row.cancellationReason}`);
              const comment = row.cancellationReasonText ? ` — ${row.cancellationReasonText}` : '';
              return `${label} (${reason}${comment})`;
            }
            return label;
          },
          sortable: false,
        }),
        createColumn({
          field: DemandCasesDataGridField.IS_RELEASABLE,
          minWidth: 130,
          renderCell: cellFactory(IconCell, ({ row }) => ({
            icon:
              row.status === TsrctSourcingCaseStatus.RELEASABLE ? (
                <CheckCircle2 className="h-5 w-5 text-success" />
              ) : row.status === TsrctSourcingCaseStatus.RELEASED ? (
                <Tooltip>
                  <TooltipTrigger
                    delay={0}
                    render={
                      <span className="inline-flex cursor-help" onClick={eventStopPropagation}>
                        <InfoIcon className="h-5 w-5 text-info" />
                      </span>
                    }
                  />
                  <TooltipContent>{tColumn('case.alreadyReleased')}</TooltipContent>
                </Tooltip>
              ) : (
                <CircleX className="h-5 w-5 text-destructive" />
              ),
          })),

          sortable: false,
        }),
        // TODO: Added by
        // createColumn({
        //   field: DemandCasesDataGridField.ADDED_BY,
        //   minWidth: 200,
        //   valueGetter: (value: RowType['addedBy']) => tCell(`addedBy.${value}`),
        // }),
        createColumn({
          field: DemandCasesDataGridField.TOTAL_VALUE,
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
          valueGetter: (_value, row): CurrencyCellValue => {
            if (row.quote) {
              return row.quote.totalDiscountedValue ?? row.quote.totalValue;
            }
            return null;
          },
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            value: row.quote?.totalDiscountedValue ?? row.quote?.totalValue,
            valueStrikeThrough: row.quote?.totalValue,
            currency: row.quote?.currency,
          })),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.DISCOUNT,
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(NumberCell, {
            preset: NumberFormatPreset.PERCENT_DECIMAL,
          }),
          sortComparator: gridNumberComparator,
          valueGetter: (_value, row) => {
            if (row.quote && row.quote.discount) {
              return row.quote.discount;
            }
            return null;
          },
        }),
        createColumn({
          field: DemandCasesDataGridField.CONTACT_FIRST_NAME,
          valueGetter: (_value, row) => row.supplier?.contact?.firstName,
          minWidth: 200,
          renderCell: cellFactory(EmptyFallbackCell),
          sortComparator: gridStringOrNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.CONTACT_LAST_NAME,
          valueGetter: (_value, row) => row.supplier?.contact?.lastName,
          minWidth: 200,
          renderCell: cellFactory(EmptyFallbackCell),
          sortComparator: gridStringOrNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.CONTACT_EMAIL,
          valueGetter: (_value, row) => row.supplier?.contact?.email,
          minWidth: 200,
          renderCell: cellFactory(EmptyFallbackCell),
          sortComparator: gridStringOrNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.ISSUES_OPEN_COUNT,
          valueGetter: (_value, row) =>
            row.issues?.filter((issue) => issue.status === TsrctIssueStatus.OPEN).length ?? 0,
          renderCell: cellFactory(NumberCell, {
            preset: NumberFormatPreset.INTEGER,
          }),
          minWidth: 150,
        }),
        createColumn({
          field: DemandCasesDataGridField.ISSUES_COMPLETED_COUNT,
          valueGetter: (_value, row) =>
            row.issues?.filter((issue) => issue.status === TsrctIssueStatus.COMPLETED).length ?? 0,
          renderCell: cellFactory(NumberCell, {
            preset: NumberFormatPreset.INTEGER,
          }),
          minWidth: 150,
        }),
        createColumn({
          field: DemandCasesDataGridField.LATEST_COMMENT,
          minWidth: 250,
          flex: 1,
          sortable: false,
          renderCell: cellFactory(TooltipCell),
          valueGetter: (_value, row) => row.latestComment?.message,
        }),
      ],
    } as DataGridConfig<RowType>;
  }, [tColumn, t]);
};
